import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import * as types from '../mutation-types';
import OpportunityPipelinesAPI from '../../api/opportunityPipelines';

export const state = {
  records: [],
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
  },
};

export const getters = {
  getPipelines($state) {
    return $state.records;
  },
  // Alias kept for the cache-once `fetch` action below.
  getRecords($state) {
    return $state.records;
  },
  getUIFlags($state) {
    return $state.uiFlags;
  },
};

export const actions = {
  get: async ({ commit }) => {
    commit(types.default.SET_OPPORTUNITY_PIPELINES_UI_FLAG, {
      isFetching: true,
    });
    try {
      const response = await OpportunityPipelinesAPI.get();
      commit(types.default.SET_OPPORTUNITY_PIPELINES, response.data);
      return response.data;
    } catch (error) {
      commit(types.default.SET_OPPORTUNITY_PIPELINES, []);
      return [];
    } finally {
      commit(types.default.SET_OPPORTUNITY_PIPELINES_UI_FLAG, {
        isFetching: false,
      });
    }
  },
  // Cache-once variant: skips the network call when records are already
  // loaded, for call sites that only need the list once (e.g. a selector).
  fetch: async ({ state: moduleState, commit }) => {
    if (moduleState.records.length > 0) {
      return;
    }

    commit(types.default.SET_PIPELINES_UI_FLAG, { isFetching: true });

    try {
      const response = await OpportunityPipelinesAPI.get();
      commit(types.default.SET_PIPELINES, response.data || []);
    } catch (error) {
      commit(types.default.SET_PIPELINES, []);
    } finally {
      commit(types.default.SET_PIPELINES_UI_FLAG, { isFetching: false });
    }
  },
  show: async (_, pipelineId) => {
    const response = await OpportunityPipelinesAPI.show(pipelineId);
    return response.data;
  },
  create: async ({ commit }, payload) => {
    commit(types.default.SET_OPPORTUNITY_PIPELINES_UI_FLAG, {
      isCreating: true,
    });
    try {
      const response = await OpportunityPipelinesAPI.create(payload);
      commit(types.default.ADD_OPPORTUNITY_PIPELINE, response.data);
      return response.data;
    } finally {
      commit(types.default.SET_OPPORTUNITY_PIPELINES_UI_FLAG, {
        isCreating: false,
      });
    }
  },
  update: async ({ commit }, { id, ...payload }) => {
    commit(types.default.SET_OPPORTUNITY_PIPELINES_UI_FLAG, {
      isUpdating: true,
    });
    try {
      const response = await OpportunityPipelinesAPI.update(id, payload);
      commit(types.default.EDIT_OPPORTUNITY_PIPELINE, response.data);
      return response.data;
    } finally {
      commit(types.default.SET_OPPORTUNITY_PIPELINES_UI_FLAG, {
        isUpdating: false,
      });
    }
  },
  toggleStatus: async ({ commit }, pipelineId) => {
    const response = await OpportunityPipelinesAPI.updateStatus(pipelineId);
    commit(types.default.EDIT_OPPORTUNITY_PIPELINE, response.data);
    return response.data;
  },
  getUsers: async (_, pipelineId) => {
    const response = await OpportunityPipelinesAPI.getUsers(pipelineId);
    return response.data;
  },
  saveUsers: async (_, { pipelineId, userIds }) => {
    const response = await OpportunityPipelinesAPI.saveUsers(
      pipelineId,
      userIds
    );
    return response.data;
  },
  removeUsers: async (_, { pipelineId, userIds }) => {
    const response = await OpportunityPipelinesAPI.removeUsers(
      pipelineId,
      userIds
    );
    return response.data;
  },
  getInboxes: async (_, pipelineId) => {
    const response = await OpportunityPipelinesAPI.getInboxes(pipelineId);
    return response.data;
  },
  saveInboxes: async (_, { pipelineId, inboxIds }) => {
    const response = await OpportunityPipelinesAPI.saveInboxes(
      pipelineId,
      inboxIds
    );
    return response.data;
  },
  removeInboxes: async (_, { pipelineId, inboxIds }) => {
    const response = await OpportunityPipelinesAPI.removeInboxes(
      pipelineId,
      inboxIds
    );
    return response.data;
  },
  getStages: async (_, pipelineId) => {
    const response = await OpportunityPipelinesAPI.getStages(pipelineId);
    return response.data;
  },
  createStage: async (_, { pipelineId, payload }) => {
    const response = await OpportunityPipelinesAPI.createStage(
      pipelineId,
      payload
    );
    return response.data;
  },
  updateStage: async (_, { pipelineId, stageId, payload }) => {
    const response = await OpportunityPipelinesAPI.updateStage(
      pipelineId,
      stageId,
      payload
    );
    return response.data;
  },
  updateStageStatus: async (_, { pipelineId, stageId, status }) => {
    const response = await OpportunityPipelinesAPI.updateStageStatus(
      pipelineId,
      stageId,
      status
    );
    return response.data;
  },
  removeStage: async (_, { pipelineId, stageId }) => {
    await OpportunityPipelinesAPI.removeStage(pipelineId, stageId);
  },
  reorderStages: async (_, { pipelineId, stages }) => {
    const response = await OpportunityPipelinesAPI.reorderStages(
      pipelineId,
      stages
    );
    return response.data;
  },
  getLossReasons: async (_, pipelineId) => {
    const response = await OpportunityPipelinesAPI.getLossReasons(pipelineId);
    return response.data;
  },
  createLossReason: async (_, { pipelineId, payload }) => {
    const response = await OpportunityPipelinesAPI.createLossReason(
      pipelineId,
      payload
    );
    return response.data;
  },
  updateLossReason: async (_, { pipelineId, lossReasonId, payload }) => {
    const response = await OpportunityPipelinesAPI.updateLossReason(
      pipelineId,
      lossReasonId,
      payload
    );
    return response.data;
  },
  getNegotiationForm: async (_, pipelineId) => {
    try {
      const response = await OpportunityPipelinesAPI.getNegotiationForm(
        pipelineId
      );
      return response.data;
    } catch (error) {
      if (error.response && error.response.status === 404) {
        return null;
      }
      throw error;
    }
  },
  createNegotiationForm: async (_, pipelineId) => {
    const response = await OpportunityPipelinesAPI.createNegotiationForm(
      pipelineId
    );
    return response.data;
  },
  updateNegotiationForm: async (_, { pipelineId, payload }) => {
    const response = await OpportunityPipelinesAPI.updateNegotiationForm(
      pipelineId,
      payload
    );
    return response.data;
  },
  deleteNegotiationForm: async (_, pipelineId) => {
    await OpportunityPipelinesAPI.deleteNegotiationForm(pipelineId);
  },
  createNegotiationQuestion: async (_, { pipelineId, payload }) => {
    const response = await OpportunityPipelinesAPI.createNegotiationQuestion(
      pipelineId,
      payload
    );
    return response.data;
  },
  updateNegotiationQuestion: async (_, { pipelineId, questionId, payload }) => {
    const response = await OpportunityPipelinesAPI.updateNegotiationQuestion(
      pipelineId,
      questionId,
      payload
    );
    return response.data;
  },
  deleteNegotiationQuestion: async (_, { pipelineId, questionId }) => {
    await OpportunityPipelinesAPI.deleteNegotiationQuestion(
      pipelineId,
      questionId
    );
  },
  reorderNegotiationQuestions: async (_, { pipelineId, questionIds }) => {
    const response = await OpportunityPipelinesAPI.reorderNegotiationQuestions(
      pipelineId,
      questionIds
    );
    return response.data;
  },
};

export const mutations = {
  [types.default.SET_OPPORTUNITY_PIPELINES_UI_FLAG]($state, uiFlag) {
    $state.uiFlags = { ...$state.uiFlags, ...uiFlag };
  },
  [types.default.SET_OPPORTUNITY_PIPELINES]: MutationHelpers.set,
  [types.default.ADD_OPPORTUNITY_PIPELINE]: MutationHelpers.create,
  [types.default.EDIT_OPPORTUNITY_PIPELINE]: MutationHelpers.update,
  [types.default.SET_PIPELINES_UI_FLAG]($state, data) {
    $state.uiFlags = { ...$state.uiFlags, ...data };
  },
  [types.default.SET_PIPELINES]: MutationHelpers.set,
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
